import { ScopedMutator } from 'swr'

/**
 * Every client list that shows a user's conversations, by SWR key. A room
 * starting, being renamed or deleted moves all of them together, so they are
 * revalidated through this one helper and never one by one at the call site.
 *
 * - `['conversation-history', search]`: the /chats page (`useConversationHistory`).
 * - `'sidebar-threads'`: the sidebar Recent rail (`SIDEBAR_THREADS_KEY`).
 * - `'home-lab:recent-chats'`: Chat mode's recent conversations on the home page.
 * - `'trial-room-count'`: the trial room counter (`useTrialChatRoomLimit`).
 *
 * The rail key lives here and `useSidebarThreads.ts` re-exports it as
 * `SIDEBAR_THREADS_KEY`, so the rail module can call this helper without an
 * import cycle.
 */
export const CONVERSATION_HISTORY_KEY_PREFIX = 'conversation-history'
export const RAIL_CONVERSATIONS_KEY = 'sidebar-threads'
export const HOME_LAB_RECENT_CHATS_KEY = 'home-lab:recent-chats'
export const TRIAL_ROOM_COUNT_KEY = 'trial-room-count'

/** The conversations list SWR keys (`['conversation-history', search]`). */
export function isConversationHistoryKey(key: unknown): boolean {
  return Array.isArray(key) && key[0] === CONVERSATION_HISTORY_KEY_PREFIX
}

export interface RevalidateConversationListsOptions {
  /**
   * False when the caller already patched the rail cache in place (rail
   * rename/delete), so the rail does not refetch what it just wrote.
   */
  includeRail?: boolean
}

/**
 * Revalidates every conversation list. Fire-and-forget: a failed refetch
 * leaves the previous data, same as any SWR revalidation.
 *
 * Callers on the guest transport skip this: guests own none of these lists.
 */
export function revalidateConversationLists(
  mutate: ScopedMutator,
  options: RevalidateConversationListsOptions = {},
): void {
  const { includeRail = true } = options
  const ignore = () => undefined
  Promise.resolve(mutate(isConversationHistoryKey)).catch(ignore)
  if (includeRail) {
    Promise.resolve(mutate(RAIL_CONVERSATIONS_KEY)).catch(ignore)
  }
  Promise.resolve(mutate(HOME_LAB_RECENT_CHATS_KEY)).catch(ignore)
  Promise.resolve(mutate(TRIAL_ROOM_COUNT_KEY)).catch(ignore)
}
